import type * as React from 'react'
import { type ListPageColumn, type ListPageRowAction } from '@/components/templates/list-page'
import { Badge } from '@/components/ui/badge'
import type {
  MembershipLevel,
  MembershipLevelDialogState,
} from '@/pages/MembershipLevelsPage.types'

export const membershipLevelColumns: ListPageColumn<MembershipLevel>[] = [
  {
    id: 'level',
    header: 'Level',
    cell: (level) => (
      <div className="flex flex-col gap-1">
        <span className="text-foreground">{level.name}</span>
        <MembershipLevelStatusBadge level={level} className="w-fit sm:hidden" />
      </div>
    ),
  },
  {
    id: 'fee',
    header: 'Fee',
    cell: (level) => (
      <span className="font-semibold tabular-nums text-foreground">
        {level.price_label ?? level.fee}
      </span>
    ),
  },
  {
    id: 'renewal',
    header: 'Renewal',
    cell: (level) => <span className="text-muted-foreground">{level.renewal}</span>,
  },
  {
    id: 'members',
    header: 'Members',
    headerClassName: 'hidden text-right md:table-cell',
    className: 'hidden text-right text-muted-foreground md:table-cell',
    cell: (level) => (
      <div className="flex flex-col items-end text-xs">
        <span>Memberships: {level.membership_count ?? level.member_count}</span>
        <span>
          People with member status: {level.people_with_member_status_count ?? level.member_count}
        </span>
      </div>
    ),
  },
  {
    id: 'join-form',
    header: 'Join form',
    headerClassName: 'hidden lg:table-cell',
    className: 'hidden lg:table-cell',
    cell: (level) => <MembershipLevelJoinFormBadges level={level} />,
  },
  {
    id: 'status',
    header: 'Status',
    headerClassName: 'hidden sm:table-cell',
    className: 'hidden sm:table-cell',
    cell: (level) => <MembershipLevelStatusBadge level={level} />,
  },
]

export function membershipLevelRowActions({
  level,
  levels,
  moveLevel,
  restoreLevel,
  setDialogState,
  setArchiveTarget,
  setDeleteTarget,
}: MembershipLevelRowActionsOptions): ListPageRowAction<MembershipLevel>[] {
  const levelIndex = levels.findIndex((candidate) => candidate.id === level.id)

  return [
    {
      label: 'Move up',
      onSelect: () => moveLevel(level, 'up'),
      disabled: levelIndex <= 0,
    },
    {
      label: 'Move down',
      onSelect: () => moveLevel(level, 'down'),
      disabled: levelIndex < 0 || levelIndex === levels.length - 1,
    },
    {
      label: 'Edit',
      onSelect: () => setDialogState({ mode: 'edit', level }),
    },
    membershipLevelVisibilityAction(level, restoreLevel, setArchiveTarget),
    {
      label: 'Delete permanently',
      destructive: true,
      onSelect: () => setDeleteTarget(level),
    },
  ]
}

function MembershipLevelJoinFormBadges({ level }: { level: MembershipLevel }) {
  return (
    <div className="flex flex-wrap gap-1">
      {level.published_to_join_form ? (
        <Badge variant="success">Join form</Badge>
      ) : (
        <Badge variant="neutral">Staff only</Badge>
      )}
      {level.requires_admin_approval && <Badge variant="warning">Approval required</Badge>}
    </div>
  )
}

export function movedMembershipLevels(
  levels: MembershipLevel[],
  level: MembershipLevel,
  direction: 'up' | 'down',
) {
  const index = levels.findIndex((candidate) => candidate.id === level.id)
  const targetIndex = direction === 'up' ? index - 1 : index + 1
  if (index < 0 || targetIndex < 0 || targetIndex >= levels.length) return null

  const nextLevels = [...levels]
  const [moved] = nextLevels.splice(index, 1)
  nextLevels.splice(targetIndex, 0, moved)
  return nextLevels
}

interface MembershipLevelRowActionsOptions {
  level: MembershipLevel
  levels: MembershipLevel[]
  moveLevel: (level: MembershipLevel, direction: 'up' | 'down') => void
  restoreLevel: (level: MembershipLevel) => void
  setDialogState: React.Dispatch<React.SetStateAction<MembershipLevelDialogState>>
  setArchiveTarget: React.Dispatch<React.SetStateAction<MembershipLevel | null>>
  setDeleteTarget: React.Dispatch<React.SetStateAction<MembershipLevel | null>>
}

function membershipLevelVisibilityAction(
  level: MembershipLevel,
  restoreLevel: (level: MembershipLevel) => void,
  setArchiveTarget: React.Dispatch<React.SetStateAction<MembershipLevel | null>>,
): ListPageRowAction<MembershipLevel> {
  if (level.archived) {
    return {
      label: 'Restore',
      onSelect: () => restoreLevel(level),
    }
  }

  return {
    label: 'Archive',
    onSelect: () => setArchiveTarget(level),
  }
}

function MembershipLevelStatusBadge({
  level,
  className,
}: {
  level: MembershipLevel
  className?: string
}) {
  if (level.archived) {
    return (
      <Badge variant="neutral" className={className}>
        Archived
      </Badge>
    )
  }

  return (
    <Badge variant="success" className={className}>
      Active
    </Badge>
  )
}
